// Localização: frontend/src/components/Prontuario/Profissional/ProfissionalRHTab.jsx (versão 1.0.0)
// Aba de Recursos Humanos do prontuário do profissional

import React, { useState, useEffect } from 'react';
import { DollarSign, CalendarX, Calendar, Clock, Award, Plus } from 'lucide-react';
import { getResumoRH, getSalarios, getFaltas, getFerias, getBancoHoras, getAvaliacoes } from '../../../api/api';
import SalariosSection from './RH/SalariosSection';
import FaltasSection from './RH/FaltasSection';
import FeriasSection from './RH/FeriasSection';
import BancoHorasSection from './RH/BancoHorasSection';
import AvaliacoesSection from './RH/AvaliacoesSection';

const ProfissionalRHTab = ({ profissionalId, prontuarioData, onRefresh }) => {
    const [resumoRH, setResumoRH] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('resumo');

    useEffect(() => {
        fetchResumoRH();
    }, [profissionalId]);

    const fetchResumoRH = async () => {
        try {
            setLoading(true);
            const response = await getResumoRH(profissionalId);
            if (response.data.success) {
                setResumoRH(response.data.data);
            }
        } catch (error) {
            console.error('Erro ao buscar resumo de RH:', error);
        } finally {
            setLoading(false);
        }
    };

    const sections = [
        { id: 'resumo', name: 'Resumo', icon: Award },
        { id: 'salarios', name: 'Salários', icon: DollarSign },
        { id: 'faltas', name: 'Faltas', icon: CalendarX },
        { id: 'ferias', name: 'Férias', icon: Calendar },
        { id: 'banco-horas', name: 'Banco de Horas', icon: Clock },
        { id: 'avaliacoes', name: 'Avaliações', icon: Award }
    ];

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                <p className="mt-2 text-gray-600">Carregando dados de RH...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Navegação de Seções */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex flex-wrap gap-2">
                    {sections.map((section) => {
                        const Icon = section.icon;
                        const isActive = activeSection === section.id;
                        return (
                            <button
                                key={section.id}
                                onClick={() => setActiveSection(section.id)}
                                className={`
                                    flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium
                                    transition-colors duration-200
                                    ${isActive
                                        ? 'bg-teal-100 text-teal-700 border border-teal-300'
                                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                                    }
                                `}
                            >
                                <Icon className="h-4 w-4" />
                                <span>{section.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Resumo de RH */}
            {activeSection === 'resumo' && resumoRH && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo de RH</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                            <p className="text-sm text-gray-600">Salário Atual</p>
                            <p className="text-2xl font-bold text-teal-700">
                                {resumoRH.salario_atual
                                    ? `R$ ${parseFloat(resumoRH.salario_atual.valor_salario || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                    : 'Não informado'
                                }
                            </p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                            <p className="text-sm text-gray-600">Faltas no Ano</p>
                            <p className="text-2xl font-bold text-red-700">{resumoRH.total_faltas_ano || 0}</p>
                            <p className="text-xs text-gray-500 mt-1">
                                {resumoRH.faltas_nao_justificadas || 0} não justificadas
                            </p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <p className="text-sm text-gray-600">Férias Agendadas</p>
                            <p className="text-2xl font-bold text-blue-700">{resumoRH.ferias_agendadas || 0}</p>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                            <p className="text-sm text-gray-600">Saldo Banco de Horas</p>
                            <p className="text-2xl font-bold text-yellow-700">
                                {parseFloat(resumoRH.saldo_banco_horas || 0).toFixed(2)}h
                            </p>
                        </div>
                    </div>
                    {resumoRH.ultima_avaliacao && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm font-medium text-gray-700">Última Avaliação</p>
                            <p className="text-sm text-gray-600">
                                {new Date(resumoRH.ultima_avaliacao.data_avaliacao).toLocaleDateString('pt-BR')} - 
                                Nota: {resumoRH.ultima_avaliacao.nota_geral || 'N/A'}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Seções Específicas */}
            {activeSection === 'salarios' && (
                <SalariosSection profissionalId={profissionalId} onRefresh={fetchResumoRH} />
            )}
            {activeSection === 'faltas' && (
                <FaltasSection profissionalId={profissionalId} onRefresh={fetchResumoRH} />
            )}
            {activeSection === 'ferias' && (
                <FeriasSection profissionalId={profissionalId} onRefresh={fetchResumoRH} />
            )}
            {activeSection === 'banco-horas' && (
                <BancoHorasSection profissionalId={profissionalId} onRefresh={fetchResumoRH} />
            )}
            {activeSection === 'avaliacoes' && (
                <AvaliacoesSection profissionalId={profissionalId} onRefresh={fetchResumoRH} />
            )}
        </div>
    );
};

export default ProfissionalRHTab;

